import { z } from 'zod'

/**
 * Mirrors `Budget/Models/TransactionModels.swift`. iOS persists via
 * CodableRepository which uses .iso8601 for dates — i.e. transaction
 * dates are ISO-8601 strings in Firestore, not Firestore Timestamps.
 * Keep the schema string-based to match what iOS writes.
 */

export const TransactionStatusSchema = z.enum(['cleared', 'uncleared', 'reconciled'])
export type TransactionStatus = z.infer<typeof TransactionStatusSchema>

export const SplitTransactionSchema = z.object({
  id: z.string().uuid(),
  transactionId: z.string().uuid(),
  categoryId: z.string().uuid(),
  amount: z.number(),
  memo: z.string().nullable().optional(),
})

export const TransactionSchema = z.object({
  id: z.string().uuid(),
  // ISO-8601 — matches iOS .iso8601 dateEncodingStrategy.
  date: z.string(),
  payeeId: z.string().uuid().nullable().optional(),
  // Display label for the payee. iOS resolves payee names by joining
  // against a separate Payee model; web inlines the name on the
  // transaction so the list can render without a second collection
  // read. The YNAB importer + Plaid sync both populate it.
  payeeName: z.string().nullable().optional(),
  categoryId: z.string().uuid().nullable().optional(),
  accountId: z.string().uuid(),
  amount: z.number(),
  memo: z.string().nullable().optional(),
  cleared: TransactionStatusSchema.default('uncleared'),
  isInflow: z.boolean().default(false),
  splits: z.array(SplitTransactionSchema).default([]),
  isTransfer: z.boolean().default(false),
  transferAccountId: z.string().uuid().nullable().optional(),
  transferId: z.string().uuid().nullable().optional(),
  isCreditCardPayment: z.boolean().default(false),
  // Import-flow metadata (optional, set by iOS ImportManager).
  originalCategory: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
})
export type Transaction = z.infer<typeof TransactionSchema>

export const AccountTypeSchema = z.enum([
  'checking',
  'savings',
  'credit_card',
  'cash',
  'tracking',
  'investment',
  'mortgage',
  'loan',
  'other_asset',
  'other_liability',
])
export type AccountType = z.infer<typeof AccountTypeSchema>

export const AccountSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  type: AccountTypeSchema,
  isOnBudget: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  isHidden: z.boolean().default(false),
  clearedBalance: z.number().default(0),
  unclearedBalance: z.number().default(0),
  workingBalance: z.number().default(0),
  interestRate: z.number().nullable().optional(),
  minimumPayment: z.number().nullable().optional(),
  // Plaid-side fields we DON'T need to validate strictly on web.
  // Pass through anything else iOS adds without breaking.
}).passthrough()
export type Account = z.infer<typeof AccountSchema>
